import React, { useState } from 'react';

/**
 * What signup shows while new accounts are paused (the API's daily cap, which
 * an invite gets past): an email and, optionally, a line about the site, sent
 * to POST /api/access-requests (staff see them in the ops portal). Never says
 * how many accounts the cap allows.
 */

const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';
const MAX = 1000;

export function RequestAccess({
  initialEmail = '',
  cardNotCharged = false,
  onEnterCode,
  onLogin,
}: {
  initialEmail?: string;
  cardNotCharged?: boolean;
  onEnterCode: () => void;
  onLogin: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [useCase, setUseCase] = useState('');
  const [trap, setTrap] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sentTo) return;
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError('Enter a valid email address');
    setSending(true);
    try {
      const res = await fetch(`${backendHost}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), useCase: useCase.trim(), website: trap }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || 'Couldn’t send that. Please try again.');
      }
      setSentTo(email.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t send that. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="flow-gate" onSubmit={submit} noValidate>
      <h1 className="flow-h1">New accounts are paused for now</h1>
      <p className="flow-lede">
        Demand has been high, so we’ve paused new accounts for a little while. Request an invite and
        we’ll let you know when there’s room for you.
        {cardNotCharged ? ' Your card was not charged.' : ''}
      </p>
      <label className="flow-field flow-gate-first">
        <span className="flow-label">Email</span>
        <input
          className="flow-input flow-input-tall"
          data-error={error ? 'true' : undefined}
          type="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.com"
          value={email}
          readOnly={Boolean(sentTo)}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
        />
      </label>
      <label className="flow-field flow-gate-next">
        <span className="flow-label">
          The site <span className="flow-label-quiet">(optional)</span>
        </span>
        <input
          className="flow-input flow-input-tall"
          maxLength={MAX}
          placeholder="A bakery in Oakland with a weekly menu"
          value={useCase}
          readOnly={Boolean(sentTo)}
          onChange={(e) => {
            setUseCase(e.target.value);
            setError(null);
          }}
        />
      </label>
      {/* Left empty by people; bots fill it in. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={trap}
        onChange={(e) => setTrap(e.target.value)}
        style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }}
      />
      {error ? (
        <span className="flow-gate-error" role="alert">
          {error}
        </span>
      ) : null}
      {sentTo ? (
        <div className="flow-gate-sent" role="status">
          <span className="flow-status-dot" />
          <span>
            Sent. Watch <strong>{sentTo}</strong> for your code.
          </span>
        </div>
      ) : (
        <button type="submit" className="flow-btn flow-gate-btn" disabled={sending}>
          {sending ? 'Sending…' : 'Request invite'}
        </button>
      )}
      <div className="flow-gate-links">
        <button type="button" className="flow-link" onClick={onEnterCode}>
          Have a code? Enter it
        </button>
        <button type="button" className="flow-link" onClick={onLogin}>
          Already have an account? Log in
        </button>
      </div>
    </form>
  );
}
